//////LIBRARY//////
import React, { useEffect, useState, Fragment, useMemo } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { bindActionCreators } from 'redux'
import { cloneDeep, isEmpty, isUndefined, map, isEqual, isNull } from 'lodash'
//////COMPONENTS//////
import Table from '../../components/Table'
import { uriList } from '../../routings/settings/RouteList'
import ActionsTable from '../../components/ActionsTable/ActionsTable'
import { telecollectManagerActionCreators, columnsActionCreators, tableActionCreators } from '../../store/actions'
import Theme from '../../theme/theme'
import DropdownCheckbox from '../../components/DropdownCheckbox'
import UseDatePicker from '../../hooks/UseDatePicker'
import { DatePickerFilterLabel } from '../../theme/GlobalCss'
import { TelecollectManagerStatus } from './Components'
import UseCheckAll from '../../hooks/UseCheckAll'
import userPrivileges from '../../constants/PrivilegesList'
import urlExport from '../../constants/UrlExport'
import TableTitle from '../../components/Table/components/TableTitle'
//////ASSETS//////
import Calendar from '../../assets/icones/global/calendar.svg'

//////STYLED//////
const TelecollectManagerWrapper = styled.div`
	position: relative;
`
const DatePickerTelecollectIcon = styled.div`
	border-radius: 4px;
	background-color: ${(props) => props.theme.colors.primary};
	height: 32px;
	width: 32px;
	padding: 6px;
	box-sizing: border-box;
	cursor: pointer;
	background-image: url(${Calendar});
	background-repeat: no-repeat;
	background-size: 20px 20px;
	background-position: center;
	:hover {
		background-color: ${(props) => props.theme.colors.hoverPrimary};
	}
	transition: background-color 0.1s;
`
//////STYLED//////

const TelecollectManager = () => {
	const { user, translations, telecollectManager, telecollectFilters, userKioskPerimeter, telecollectId, currentColumn, currentDir } = useSelector(
		(state) => ({
			translations: state.languages.translations,
			telecollectManager: state.telecollectManager,
			telecollectFilters: state.telecollectManager.header || false,
			user: state.user,
			userKioskPerimeter: state.user.identity.kioskSelected,
			telecollectId: state.telecollectManager.telecollectId,
			currentColumn: state.columns.currentColumn,
			currentDir: state.columns.currentDir,
		}),
		shallowEqual
	)

	const actionsTelecollectManager = bindActionCreators(telecollectManagerActionCreators, useDispatch())
	const actionsColumns = bindActionCreators(columnsActionCreators, useDispatch())
	const actionsTable = bindActionCreators(tableActionCreators, useDispatch())

	// Filters
	const [prevFilter, setPrevFilter] = useState({})
	const [filters, setFilters, handleCheckElements] = UseCheckAll(prevFilter)

	// Data
	const [paging, setPaging] = useState({ page: '0', size: '250', sort: 'date', nameDir: 'desc' })

	const [currentPerimeterKiosks, setCurrentPerimeterKiosks] = useState([])

	const [
		dateTelecollectShowModal,
		dateTelecollectOpenDateModalHandler,
		dateTelecollectRenderModal,
		dateTelecollectStateDate,
		setStartDate,
		setEndDate,
	] = UseDatePicker({
		defaultDateStart: moment().startOf('day').subtract(15, 'days').toDate(),
		defaultDateEnd: moment().hours(23).minutes(59).toDate(),
	})

	// Columns
	const [columns, setColumns] = useState([])

	/////////////////////////////// HANDLER //////////////////////////////////////

	const handlePagingTelecollects = (paging) => {
		const pg = {
			...paging,
			...{
				sort: !isUndefined(currentColumn.value) ? currentColumn.value : 'date',
				nameDir: !isUndefined(currentDir.value) ? currentDir.value : 'desc',
			},
		}
		setPaging(pg)
		actionsTelecollectManager.getTelecollects(handleFilterSend(), pg)
	}

	const handleSubmit = () => {
		if (isEqual(filters, prevFilter)) return // guard to call if no change

		setPrevFilter(filters)

		// Force reset paging at zero, in case when filters are preventing any pagination with current size
		let pg = cloneDeep(paging)
		if (isUndefined(pg)) {
			pg = { page: '0', size: '250', sort: 'date', nameDir: 'desc' }
		} else {
			pg.page = '0'
		}
		setPaging(pg)

		actionsTelecollectManager.getTelecollects(handleFilterSend(), pg)
	}

	const handleFilterSend = () => {
		return {
			withFilters: true,
			datesRange: {
				// Dummy dates
				dateStart: dateTelecollectStateDate.date.startDate,
				dateEnd: dateTelecollectStateDate.date.endDate,
			},
			userKioskScope: {
				kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
			},
			...filters,
		}
	}

	const handleSeeTelecollectTicket = (rowId) => {
		//history.push(`${uriList.telecollectDetail}/${rowId}`)
		window.open(`${uriList.sales}${uriList.telecollectManager}${uriList.telecollectTicket}/${rowId}`, '_blank')
	}

	/////////////////////////////// CONFIG //////////////////////////////////////

	useEffect(() => {
		setColumns([
			{
				columnReferential: 'paymentCollection_store',
				dataHook: 'storeLabelToDisplay',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_storeLabel'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'kiosk.store.company.labelToDisplay',
			},
			{
				columnReferential: 'paymentCollection_kioskCode',
				dataHook: 'kioskCode',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_kioskCode'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'kiosk.labelToManufacturer',
			},
			{
				columnReferential: 'paymentCollection_kioskName',
				dataHook: 'kioskName',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_kioskName'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'kiosk.description',
			},
			{
				columnReferential: 'paymentCollection_clientKioskName',
				dataHook: 'customerKioskCode',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_kioskCodeCustomer'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'kiosk.labelToOwner',
			},
			{
				columnReferential: 'paymentCollection_dateHour',
				dataHook: 'dateCreated',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_dateCreated'],
				sortable: true,
				isAuthorize: true,
				component: (props) => (!isNull(props.row.dateCreated) ? moment(props.row.dateCreated).format('DD/MM/YYYY HH:mm') : '-'),
				sortableId: 'date',
			},
			{
				columnReferential: 'paymentCollection_telecollectionNumber',
				dataHook: 'number',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_number'],
				sortable: true,
				isAuthorize: true,
				sortableId: 'number',
			},
			{
				columnReferential: 'paymentCollection_state',
				dataHook: 'paymentCollectionStatus.translationKey',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_paymentCollectionStatus'],
				sortable: true,
				isAuthorize: true,
				component: TelecollectManagerStatus,
				sortableId: 'status.description',
			},
			{
				columnReferential: 'paymentCollection_buyCancel',
				dataHook: 'buyQty',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_buyQty_cancelQty'],
				sortable: true,
				isAuthorize: true,
				component: (props) => `${props.row.buyQty} - ${props.row.cancelQty}`,
				sortableId: 'purchaseQty',
			},
			{
				columnReferential: 'paymentCollection_amountTTC',
				dataHook: 'totalAmountTTC',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_paymentCollectionAmountTTC'],
				sortable: true,
				isAuthorize: true,
				component: (props) => (!isNull(props.row.totalAmountTTC) ? `${props.row.totalAmountTTC}${props.row.currency.symbol}` : '-'),
				sortableId: 'totalTtc',
			},
			/*{
				columnReferential: 'paymentCollection_comparisonStatus', dataHook: 'comparisonStatus.translationKey',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_comparison'],
				sortable: true,
				isAuthorize: true,
				component: TelecollectManagerComparisonStatus,
			},*/
			/* [147] Masking this column also
				{
				columnReferential: 'paymentCollection_isComparisonAuto', dataHook: 'isComparisonAuto',
				headerName: translations['paymentCollectionManagement__admin__paymentsCollectionTable_tableHeader_comparisonAuto'],
				sortable: true,
				isAuthorize: true,
				component: (props) => (props.row.isComparisonAuto ? translations['common__text__yes'] : '-'),
			},*/
			{
				action: true,
				isFixed: true,
				component: ActionsTable,
				withIcons: false,
				rest: {
					tooltipPart: 'telecollect',
					paperActionsList: [
						{
							//iconDef: telecollectSeeTicketIcon,
							name: translations['page__admin__paymentCollectionManagement__actions_toSeeTicket'],
							translationKey: 'page__admin__paymentCollectionManagement__actions_toSeeTicket',
							clickAction: (row) => {
								handleSeeTelecollectTicket(row.id)
							},
							isVisible: (row) => !isNull(row.ticket),
						},
					],
				},
				checkRight: userPrivileges.view[14],
			},
		])
	}, [translations])

	// Used for columns displaying (redux stored)
	const tableReferential = {
		idPageTable: 12,
		labelPageTable: 'paymentCollection',
	}

	/////////////////////////////// USE EFFECT //////////////////////////////////////

	const [configTableTitle, setConfigTableTitle] = useState({
		paging: {
			fnSendTo: (paging) => handlePagingTelecollects(paging),
		},
	})
	const [configTable, setConfigTable] = useState({})

	useEffect(() => {
		const cfg = cloneDeep(configTableTitle)
		if (!isEmpty(telecollectManager) && !isUndefined(telecollectManager)) {
			cfg.paging = {
				totalElements: telecollectManager.paging.totalElements,
				totalPages: telecollectManager.paging.totalPages,
				fnSendTo: (paging) => handlePagingTelecollects(paging),
			}
			setConfigTableTitle(cfg)
		}
	}, [telecollectManager])

	useEffect(() => {
		const filtersCp = cloneDeep(telecollectFilters)

		filtersCp.withFilters = true

		setFilters(filtersCp)
		setPrevFilter(filtersCp)
	}, [telecollectFilters])

	useEffect(() => {
		let cfg = cloneDeep(configTableTitle)
		cfg.name = translations['page__paymentCollectionManagement__title']
		cfg.actions = {}

		cfg.actions.rest = [
			() => (
				<Fragment>
					<label style={{ color: Theme.colors.table.body.text, marginRight: '10px', lineHeight: '32px' }}>
						{translations['page__paymentCollectionManagement__admin__header__datePicker__paymentCollectionDate']} :
					</label>
					<DatePickerTelecollectIcon onClick={dateTelecollectOpenDateModalHandler} />
					<DatePickerFilterLabel>
						<span>
							{translations['page__global__admin__header_datePicker_from']}{' '}
							{isEqual(dateTelecollectStateDate.date.startDate, {})
								? moment(new Date()).subtract(6, 'days').format('DD/MM/YYYY HH:mm')
								: moment(dateTelecollectStateDate.date.startDate).format('DD/MM/YYYY HH:mm')}
						</span>
						<span>
							{translations['page__global__admin__header_datePicker_to']}{' '}
							{isEqual(dateTelecollectStateDate.date.endDate, {})
								? moment(new Date()).format('DD/MM/YYYY HH:mm')
								: moment(dateTelecollectStateDate.date.endDate).format('DD/MM/YYYY HH:mm')}
						</span>
					</DatePickerFilterLabel>
				</Fragment>
			),
			// [147] Masking those filters
			/*() => (<DropdownCheckbox
				title={ translations['page__paymentCollectionManagement__admin__header__filter_auto'] }
				data={ filters.filtersSelection?.paymentCollectionComparisonTypes.filterElements || [] }
				translations={ translations }
				paperTopPosition={ '40' }
				dropColor={'blueColor'}
				selectActionsHandler={ handleCheckElements }
				submitOnClose={handleSubmit}
				target={'paymentCollectionComparisonTypes'}
				toTranslate={true}
			/>
			),
			() => (
				<DropdownCheckbox
					title={translations['page__paymentCollectionManagement__admin__header__filter_comparisonStatus']}
					data={filters.filtersSelection?.paymentCollectionComparisonStatus.filterElements || []}
					translations={translations}
					paperTopPosition={'40'}
					dropColor={'blueColor'}
					selectActionsHandler={handleCheckElements}
					submitOnClose={handleSubmit}
					target={'paymentCollectionComparisonStatus'}
					toTranslate={true}
				/>
			),*/
			() => (
				<DropdownCheckbox
					title={translations['page__paymentCollectionManagement__admin__header__filter_status']}
					data={filters.filtersSelection?.paymentCollectionStatus.filterElements || []}
					translations={translations}
					paperTopPosition={'40'}
					dropColor={'blueColor'}
					selectActionsHandler={handleCheckElements}
					submitOnClose={handleSubmit}
					target={'paymentCollectionStatus'}
					toTranslate={true}
				/>
			),
			() => (
				<DropdownCheckbox
					title={translations['page__paymentCollectionManagement__salesTable_tableHeader_kioskCustomerCode']}
					data={filters.filtersSelection?.kiosks.filterElements || []}
					translations={translations}
					paperTopPosition={'40'}
					dropColor={'blueColor'}
					selectActionsHandler={handleCheckElements}
					submitOnClose={handleSubmit}
					target={'kiosks'}
				/>
			),
			() => (
				<DropdownCheckbox
					title={translations['page__paymentCollectionManagement__admin__header__filter_store']}
					data={filters.filtersSelection?.stores.filterElements || []}
					translations={translations}
					paperTopPosition={'40'}
					dropColor={'blueColor'}
					selectActionsHandler={handleCheckElements}
					submitOnClose={handleSubmit}
					target={'stores'}
				/>
			),
		]
		cfg.paging = {
			totalElements: telecollectManager.paging.totalElements,
			totalPages: telecollectManager.paging.totalPages,
			fnSendTo: (paging) => handlePagingTelecollects(paging),
		}
		setConfigTableTitle(cfg)
	}, [translations, filters, dateTelecollectStateDate])

	useEffect(() => {
		const cfgCp = cloneDeep(configTable)
		cfgCp.export = {
			privilege: userPrivileges.export[10],
			url: urlExport[3],
			bd: {
				filtersSelection: filters.filtersSelection,
				withFilters: true,
				datesRange: { dateEnd: dateTelecollectStateDate.date.endDate, dateStart: dateTelecollectStateDate.date.startDate },
				userKioskScope: {
					kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
				},
			},
		}

		setConfigTable(cfgCp)
	}, [translations, filters])

	useEffect(() => {
		if (dateTelecollectStateDate.showModal) {
			actionsTelecollectManager.getTelecollects(handleFilterSend(), paging)
		}
	}, [dateTelecollectShowModal])

	const [swytch, setSwytch] = useState(false)

	useEffect(() => {
		if (!isUndefined(user) || !isEmpty(telecollectId)) {
			setSwytch(true)
		}
	}, [user, telecollectId])

	useEffect(() => {
		if (isUndefined(user) || isEmpty(translations)) return
		if (swytch) {
			const filtersCp = {
				//'idPaymentCollection' : 1,  //just for fix
				withFilters: false,
				userKioskScope: {
					kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
				},
				datesRange: {
					// Dummy dates
					dateStart: moment().startOf('day').subtract(15, 'days').toDate(),
					dateEnd: moment().hours(23).minutes(59).toDate(),
				},
			}

			if (!isEqual(telecollectId, {})) {
				filtersCp.idPaymentCollection = telecollectId.paymentCollectionId

				// For request
				filtersCp.datesRange.dateStart = moment(telecollectId.paymentCollectionDate).subtract(3, 'hours')
				filtersCp.datesRange.dateEnd = moment(telecollectId.paymentCollectionDate).add(3, 'hours')

				// And for state of datepicker
				setStartDate(moment(filtersCp.datesRange.dateStart).toDate())
				setEndDate(moment(filtersCp.datesRange.dateEnd).toDate())
			}

			actionsTelecollectManager.getTelecollects(filtersCp)
			actionsColumns.setColumns([])
			setFilters(filtersCp)
		}
	}, [swytch, translations])

	// If user is changing his perimeter, we need to make a back call
	useEffect(() => {
		if (!isEqual(userKioskPerimeter, currentPerimeterKiosks) && !isEmpty(currentPerimeterKiosks))
			actionsTelecollectManager.getTelecollects(
				{
					withFilters: false,
					datesRange: {
						// Dummy dates
						dateStart: moment().startOf('day').subtract(15, 'days').toDate(),
						dateEnd: moment().hours(23).minutes(59).toDate(),
					},
					userKioskScope: {
						kiosksInLocalScope: map(user.identity.kioskSelected, 'id'),
					},
				},
				paging
			)
		setCurrentPerimeterKiosks(userKioskPerimeter)
		setStartDate(moment().startOf('day').subtract(15, 'days').toDate())
		setEndDate(moment().hours(23).minutes(59).toDate())
	}, [userKioskPerimeter])

	useEffect(() => {
		return function cleanUp() {
			actionsTelecollectManager.resetTelecollectItems()
			actionsTable.resetPaggingConfig()
		}
	}, [])

	///////////////////////////////// RENDER ///////////////////////////////////////

	const TableRender = useMemo(() => {
		return (
			<Table
				inputColumns={columns}
				inputData={telecollectManager.items}
				inputConfig={configTable}
				currentPaging={paging}
				isLoading={telecollectManager.isLoading}
				tableReferential={tableReferential}
				actionSort={actionsTelecollectManager.getTelecollects}
			/>
		)
	}, [telecollectManager, columns, configTable, translations, paging])
	return (
		<Fragment>
			{dateTelecollectRenderModal()}
			<TelecollectManagerWrapper>
				{!isUndefined(translations) && !isUndefined(configTableTitle) && (
					<TableTitle inputConfig={configTableTitle} inputColumns={columns} tableReferential={tableReferential} />
				)}
				{!isUndefined(translations) && !isUndefined(configTable) && TableRender}
			</TelecollectManagerWrapper>
		</Fragment>
	)
}

TelecollectManager.propTypes = {}

TelecollectManager.defaultPropTypes = {}

export default TelecollectManager
